/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalEditor,
    LexicalNode,
    NodeKey,
    SerializedEditor,
    SerializedLexicalNode,
    Spread,
} from 'lexical';

import {$applyNodeReplacement, createEditor, DecoratorNode} from 'lexical';
import * as React from 'react';
import {Suspense} from 'react';

const ImageListComponent = React.lazy(
    // @ts-ignore
    () => import('./ImageListComponent'),
);

export interface ImageListPayload {
    images: string[];
    activeIndex: number;
    caption?: LexicalEditor;
    height?: number;
    key?: NodeKey;
    maxWidth?: number;
    showCaption?: boolean;
    width?: number;
    captionsEnabled?: boolean;
}

function convertImageListElement(domNode: Node): null | DOMConversionOutput {
    if (domNode instanceof HTMLDivElement) {
        const images = Array.from(domNode.getElementsByTagName('img')).map((i) => i.src);
        const {width, height, carouselId} = {
            width: parseInt(domNode.attributes.getNamedItem('width')?.value ?? ''),
            height: parseInt(domNode.attributes.getNamedItem('height')?.value ?? ''),
            carouselId: domNode.id,
        };
        const activeIndex = domNode.attributes.getNamedItem("activeIndex")?.value ?? "0";
        const node = $createImageListNode(
            {
                images: images,
                activeIndex: parseInt(activeIndex),
                height,
                width
            });
        return {node};
    }
    return null;
}

export type SerializedImageListNode = Spread<
    {
        images: string[];
        activeIndex: number;
        caption: SerializedEditor;
        height?: number;
        maxWidth: number;
        showCaption: boolean;
        width?: number;
    },
    SerializedLexicalNode
>;

export class ImageListNode extends DecoratorNode<JSX.Element> {
    __images: string[];
    __activeIndex: number;
    __width: 'inherit' | number;
    __height: 'inherit' | number;
    __maxWidth: number;
    __showCaption: boolean;
    __caption: LexicalEditor;
    // Captions cannot yet be used within editor cells
    __captionsEnabled: boolean;

    static getType(): string {
        return 'image-list';
    }

    static clone(node: ImageListNode): ImageListNode {
        return new ImageListNode(
            node.__images,
            node.__activeIndex,
            node.__maxWidth,
            node.__width,
            node.__height,
            node.__showCaption,
            node.__caption,
            node.__captionsEnabled,
            node.__key,
        );
    }

    static importJSON(serializedNode: SerializedImageListNode): ImageListNode {
        const {images, activeIndex, height, width, maxWidth, caption, showCaption} =
            serializedNode;
        const node = $createImageListNode({
            images,
            activeIndex,
            height,
            maxWidth,
            showCaption,
            width,
        });
        const nestedEditor = node.__caption;
        const editorState = nestedEditor.parseEditorState(caption.editorState);
        if (!editorState.isEmpty()) {
            nestedEditor.setEditorState(editorState);
        }
        return node;
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement('div');
        element.setAttribute('width', this.__width.toString());
        element.setAttribute('height', this.__height.toString());

        const img = document.createElement('img');
        img.setAttribute('src', this.__images[this.__activeIndex]);
        img.setAttribute('alt', "Image");
        element.append(img);

        return {element};
    }

    static importDOM(): DOMConversionMap | null {
        return {
            img: (node: Node) => ({
                conversion: convertImageListElement,
                priority: 0,
            }),
        };
    }

    constructor(
        images: string[],
        activeIndex: number,
        maxWidth: number,
        width?: 'inherit' | number,
        height?: 'inherit' | number,
        showCaption?: boolean,
        caption?: LexicalEditor,
        captionsEnabled?: boolean,
        key?: NodeKey,
    ) {
        super(key);
        this.__images = images;
        this.__activeIndex = activeIndex;
        this.__maxWidth = maxWidth;
        this.__width = width || 'inherit';
        this.__height = height || 'inherit';
        this.__showCaption = showCaption || false;
        this.__caption = caption || createEditor();
        this.__captionsEnabled = captionsEnabled || captionsEnabled === undefined;
    }

    exportJSON(): SerializedImageListNode {
        return {
            images: this.__images,
            activeIndex: this.__activeIndex,
            caption: this.__caption.toJSON(),
            height: this.__height === 'inherit' ? 0 : this.__height,
            maxWidth: this.__maxWidth,
            showCaption: this.__showCaption,
            type: 'image-list',
            version: 1,
            width: this.__width === 'inherit' ? 0 : this.__width,
        };
    }

    setWidthAndHeight(
        width: 'inherit' | number,
        height: 'inherit' | number,
    ): void {
        const writable = this.getWritable();
        writable.__width = width;
        writable.__height = height;
    }

    setImages(
        images: string[]
    ): void {
        const writable = this.getWritable();
        writable.__images = images;
    }

    setActiveImage(
        activeIndex: number
    ): void {
        const writable = this.getWritable();
        writable.__activeIndex = activeIndex;
    }

    setShowCaption(showCaption: boolean): void {
        const writable = this.getWritable();
        writable.__showCaption = showCaption;
    }

    // View

    createDOM(config: EditorConfig): HTMLElement {
        const span = document.createElement('span');
        const theme = config.theme;
        const className = theme.image;
        if (className !== undefined) {
            span.className = className;
        }
        return span;
    }

    updateDOM(): false {
        return false;
    }

    decorate(): JSX.Element {
        return (
            <Suspense fallback={null}>
                <ImageListComponent
                    images={this.__images}
                    activeIndex={this.__activeIndex}
                    width={this.__width}
                    height={this.__height}
                    maxWidth={this.__maxWidth}
                    nodeKey={this.getKey()}
                    showCaption={this.__showCaption}
                    caption={this.__caption}
                    captionsEnabled={this.__captionsEnabled}
                    resizable={true}
                />
            </Suspense>
        );
    }
}

export function $createImageListNode({
                                         images,
                                         activeIndex,
                                         height,
                                         maxWidth = 500,
                                         captionsEnabled,
                                         width,
                                         showCaption,
                                         caption,
                                         key,
                                     }: ImageListPayload): ImageListNode {
    return $applyNodeReplacement(
        new ImageListNode(
            images,
            activeIndex,
            maxWidth,
            width,
            height,
            showCaption,
            caption,
            captionsEnabled,
            key,
        ),
    );
}

export function $isImageListNode(
    node: LexicalNode | null | undefined,
): node is ImageListNode {
    return node instanceof ImageListNode;
}
